import React, { useEffect, useRef, useState } from "react";

import html2canvas from "html2canvas";

import { useEditor, useLoader } from "../../hooks";

import { debounce } from "../../helpers";

import { ErrorWrapper, CustomDropdown, Button } from "../decorate";

import { FONTS, TOOLBAR_OPTIONS, COLOR_OPTIONS } from "../../constants";

function CustomEditor({ id = null, setUrl = () => {} }) {
  const editorRef = useRef(null);
  const { activeFormats, setFormats } = useEditor();
  const { onLoader } = useLoader(false);
  const [isTyping, setIsTyping] = useState(false);

  const formats =
    !id || !activeFormats?.[id]
      ? activeFormats?.["common"]
      : activeFormats?.[id];

  const handleFormats = (data = {}) => {
    setFormats({
      [!id ? "common" : id]: {
        ...formats,
        ...data,
      },
    });
  };

  const generateImage = () => {
    if (!formats?.text || !editorRef.current) return;

    const textarea = editorRef.current;
    const textareaStyle = window.getComputedStyle(textarea);

    const cloneDiv = document.createElement("div");

    const textContent = textarea.value;

    const textColor = textareaStyle.color;
    const isNearWhite =
      textColor === "rgb(236, 231, 234)" || textColor === "rgb(255, 255, 255)";

    const allStyles = Array.from(textareaStyle);
    allStyles.forEach((property) => {
      cloneDiv.style[property] = textareaStyle.getPropertyValue(property);
    });

    cloneDiv.style.whiteSpace = "pre-wrap";
    cloneDiv.style.overflow = "visible";
    cloneDiv.style.width = `${textarea.clientWidth}px`;
    cloneDiv.style.height = "auto";
    cloneDiv.style.minHeight = `${textarea.clientHeight}px`;

    let processedContent = textContent || "";

    const hasUnderline = textareaStyle.textDecoration?.includes?.("underline");
    if (hasUnderline) {
      cloneDiv.style.textDecoration = "underline";
      const fontFamily = textareaStyle.fontFamily;

      cloneDiv.style.lineHeight =
        fontFamily.includes("Arial") ||
        fontFamily.includes("Times New Roman") ||
        fontFamily.includes("Courier New") ||
        fontFamily.includes("Montserrat") ||
        fontFamily.includes("Oswald") ||
        fontFamily.includes("Pacifico")
          ? "1.2"
          : "1";

      processedContent = processedContent.replaceAll(
        /[qzgpj97yf$@]/g,
        (match) => `<span style="
              display: inline-block;
              color: ${textareaStyle.color};
              position: relative;
              text-decoration: underline;
            ">${match}<span style="
              position: absolute;
              left: 0;
              top: 0;
              text-shadow: 0px 0px 5px ${
                textareaStyle.color === "rgb(255, 255, 255)" ? "black" : "grey"
              };
              color: ${textareaStyle.color};
            ">${match}</span></span>`
      );
    }

    cloneDiv.innerHTML = processedContent;

    if (isNearWhite) cloneDiv.style.backgroundColor = "#333333";

    cloneDiv.style.position = "absolute";
    cloneDiv.style.left = "-9999px";
    cloneDiv.style.top = "0";

    cloneDiv.style.textAlign = "center";
    document.body.appendChild(cloneDiv);

    debounce(() => {
      html2canvas(cloneDiv, {
        scale: 10,
        useCORS: true,
        backgroundColor: cloneDiv.style.backgroundColor,
        logging: false,
      })
        .then((canvas) => {
          const dataUrl = canvas.toDataURL("image/png");
          setUrl(dataUrl);
          document.body.removeChild(cloneDiv);
        })
        .catch((error) => {
          console.error("Error generating image:", error);
          document.body.removeChild(cloneDiv);
        });
    }, 100)();
  };

  useEffect(() => {
    if (!isTyping) generateImage();
  }, [formats, isTyping]);

  return (
    <div
      className="p-2 editor"
      style={{ backgroundColor: "#FFFFFF", borderRadius: 12 }}
    >
      <h5 className="text-center" style={{ color: "#6C7275", marginBottom: 2 }}>
        Add Text
      </h5>

      <Compartment
        component={
          <div className="d-flex justify-content-center align-items-center">
            {TOOLBAR_OPTIONS.map(({ command, icon, key, label }, i) => {
              return (
                <Button
                  key={command}
                  onClick={() => {
                    handleFormats({
                      textStyles: {
                        ...(formats?.textStyles || {}),
                        [key]:
                          formats?.textStyles?.[key] !== command
                            ? command
                            : null,
                      },
                    });
                  }}
                  style={{
                    margin: "0px 2px",
                    backgroundColor: "transparent",
                    color: "black",
                    padding: "5px 8px",
                    borderRadius: 8,
                    border:
                      formats?.textStyles?.[key] === command
                        ? "1px solid lightGrey"
                        : "1px solid transparent",
                  }}
                  icon={icon}
                  label={label || ""}
                />
              );
            })}
          </div>
        }
      />

      <div className="text-center">
        <textarea
          ref={editorRef}
          value={formats?.text}
          onChange={(e) => {
            setIsTyping(true);
            handleFormats({ text: e.target.value });

            debounce(() => setIsTyping(false), 300)();
          }}
          style={{
            boxShadow: "0px 0px 1px grey",
            border: "none",
            outline: "none",
            padding: "15px 10px",
            minWidth: 320,
            maxWidth: "100%",
            overflowY: "auto",
            backgroundColor: "#f7f7f7",
            borderRadius: 8,
            fontFamily: formats?.font,
            fontSize: 15,
            color: formats?.color,
            ...(formats?.textStyles || {}),
          }}
        />
      </div>
      <ErrorWrapper style={{ marginBottom: 0 }} name="editor" />

      <Compartment
        headerClass={"ml-1"}
        header={
          <div className="d-flex justify-content-between">
            <CustomDropdown
              isFilter
              placeholder="Choose Font"
              optionLabel="label"
              options={FONTS}
              style={{
                border: "none",
                boxShadow: "none",
                color: "black !important",
              }}
              value={formats?.font}
              valueTemplate={() => <>Choose Font</>}
              onChange={(e) => {
                handleFormats({
                  font: e.value,
                });
              }}
              optionTemplate={(option) => (
                <span style={{ fontFamily: option?.value }}>
                  {option?.label}
                </span>
              )}
            />
          </div>
        }
        component={
          <div
            className="font_preview m-3 text-center"
            style={{ fontFamily: formats?.font, fontSize: 20 }}
          >
            {formats?.font}
          </div>
        }
      />

      <Compartment
        header={<span style={{ marginLeft: 9 }}>Choose Colour</span>}
        component={
          <div className="d-flex flex-wrap" style={{ marginLeft: 17 }}>
            {COLOR_OPTIONS?.map(({ label, value }, i) => {
              return (
                <div
                  key={i}
                  className="d-flex flex-column justify-content-center align-items-center"
                  style={{ width: 50 }}
                >
                  <Button
                    style={{
                      padding: 0,
                      backgroundColor: value,
                      height: "25px",
                      width: "25px",
                      borderRadius: 50,
                      border:
                        formats?.color === value
                          ? `1.5px solid ${
                              formats?.color === "#000000" ? "grey" : "black"
                            }`
                          : "1.5px solid lightGrey",
                    }}
                    onClick={() => {
                      handleFormats({ color: value });
                    }}
                  />
                  <label style={{ fontSize: 12 }}>{label}</label>
                </div>
              );
            })}
          </div>
        }
      />
    </div>
  );
}

export default CustomEditor;

const Compartment = ({
  headerClass = null,
  header = null,
  component = null,
}) => {
  return (
    <>
      <div className={headerClass || "ml-3"} style={{ fontSize: 14 }}>
        {header}
      </div>
      {component}
      <hr style={{ margin: "3px 0px" }} />
    </>
  );
};
